import { animate } from "@theme/animation";
import { ThemeEvents } from "@theme/events";
import { prefersReducedMotion } from "@theme/utilities";

class PageTransition extends HTMLElement {
  constructor() {
    super();

    // Hard-refresh fallback: pjax-excluded navigations (checkout, account,
    // external) still trigger beforeunload — show the overlay to mask the unload.
    window.addEventListener("beforeunload", () => {
      document.body.classList.add("page-loading");
      this.#show();
      document.dispatchEvent(
        new CustomEvent(ThemeEvents.pageTransitionStart, {
          bubbles: true,
          detail: { direction: "out", timestamp: Date.now() },
        })
      );
    });

    // A page load isn't covered: the layout renders the overlay [hidden], so
    // the first screen paints as soon as the browser can, not once every
    // module has run and a second's fade has played. Listeners still hear
    // that the page is in and that nothing covers it.
    const initOnLoad = () => {
      this.#announce(ThemeEvents.pageLoaded);
      this.#announce(ThemeEvents.pageTransitionEnd);
    };

    if (document.readyState === "loading") {
      window.addEventListener("DOMContentLoaded", initOnLoad, { once: true });
    } else {
      initOnLoad();
    }

    // A page the back/forward cache kept comes back as beforeunload left it:
    // covered, and with the transition it announced still under way.
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        document.body.classList.remove("page-loading");
        animate(this, { opacity: 0 }, { duration: 0 });
        this.hidden = true;
        this.#announce(ThemeEvents.pageTransitionEnd);
      }
    });

    // pjax-init.js dispatches start/end; we just animate.
    document.addEventListener(ThemeEvents.pageTransitionStart, () => this.#show());
    document.addEventListener(ThemeEvents.pageTransitionEnd, () => this.#hide());
  }

  #show() {
    this.hidden = false;
    const duration = prefersReducedMotion() ? 0 : 0.25;
    animate(this, { opacity: 1 }, { duration, allowWebkitAcceleration: true });
  }

  #hide() {
    if (this.hidden) return;
    const duration = prefersReducedMotion() ? 0 : 1;
    const finish = () => {
      this.hidden = true;
    };
    animate(this, { opacity: 0 }, { duration, allowWebkitAcceleration: true })
      .finished.then(finish)
      .catch(finish);
  }

  #announce(type) {
    document.dispatchEvent(new CustomEvent(type, { bubbles: true, detail: { timestamp: Date.now() } }));
  }
}

if (!customElements.get("page-transition")) {
  customElements.define("page-transition", PageTransition);
}
